// 1) Mapa inmutable de estados (IDs del backend)
export const ExpiredColorStatus = {
  expireSoon: 1,
  expired: 2,
  ok: 3
} as const

// 2) Tipo de los IDs (1 | 2 | 3)
export type ExpiredColorStatusId =
  (typeof ExpiredColorStatus)[keyof typeof ExpiredColorStatus]

// 3) (Opcional) etiquetas para UI
export const ExpiredColorStatusLabel: Record<ExpiredColorStatusId, string> = {
  [ExpiredColorStatus.expireSoon]: 'Próximo a vencer',
  [ExpiredColorStatus.expired]: 'Vencido',
  [ExpiredColorStatus.ok]: 'OK'
}

// 4) Estilo por estado (usa los IDs, no strings)
export const getStyleRow = (
  expiredState: ExpiredColorStatusId
): React.CSSProperties => {
  if (expiredState === ExpiredColorStatus.expireSoon) {
    return { backgroundColor: '#fff3e0', borderBottom: '1px solid #ccc', color: '#e79f31' }
  }
  if (expiredState === ExpiredColorStatus.expired) {
    return { backgroundColor: '#ffebee', borderBottom: '1px solid #ccc', color: '#c62828' }
  }
  return { backgroundColor: 'white', borderBottom: '1px solid #ccc', color: '#5C8E2F' }
}

// helpers de fecha: normaliza al inicio del día (local)
const startOfDay = (d: Date) => {
  const x = new Date(d)
  x.setHours(0, 0, 0, 0)
  return x
}

// 1) Función PURA (inyectas "hoy")
export function getExpiredStatus(
  expirationDate: Date | string,
  today: Date,
  daysBefore: number = 0
): ExpiredColorStatusId {
  const exp =
    typeof expirationDate === 'string'
      ? new Date(expirationDate)
      : expirationDate
  const exp0 = startOfDay(exp)
  const today0 = startOfDay(today)

  if (exp0 < today0) return ExpiredColorStatus.expired

  const limit = new Date(today0)
  limit.setDate(limit.getDate() + daysBefore)

  if (exp0 <= limit && daysBefore > 0) return ExpiredColorStatus.expireSoon

  return ExpiredColorStatus.ok
}

// 2) Wrapper conveniente (usa la fecha actual)
export function getExpiredStatusToday(
  expirationDate: Date | string,
  daysBefore: number | undefined
): ExpiredColorStatusId {
  return getExpiredStatus(expirationDate, new Date(), daysBefore)
}

// (opcional) estilo calculando desde fecha de expiración
export const getStyleRowFromDate = (
  expirationDate: Date | string,
  daysBefore: number
): React.CSSProperties =>
  getStyleRow(getExpiredStatusToday(expirationDate, daysBefore))

// Helper para obtener color de texto según estado de vencimiento
export const getTextColor = (expiredState: ExpiredColorStatusId): string => {
  if (expiredState === ExpiredColorStatus.expired) {
    return '#c62828' // Rojo oscuro para fondo rojo pastel
  }
  if (expiredState === ExpiredColorStatus.expireSoon) {
    return '#e79f31' // Naranja oscuro para fondo naranja pastel
  }
  return '#5C8E2F' // Verde por defecto
}

// Helper para convertir IDs del enum eBatchExpiredStatus a ExpiredColorStatus
// Esto es necesario porque el backend usa IDs del enum eBatchExpiredStatus (1=Vigente, 2=Próximo, 3=Vencido)
// pero nuestro sistema de colores usa ExpiredColorStatus (1=expireSoon, 2=expired, 3=ok)
export const mapBatchStatusToExpiredColor = (
  batchStatus: number
): ExpiredColorStatusId => {
  switch (batchStatus) {
    case 1: // eBatchExpiredStatus.Vigente
      return ExpiredColorStatus.ok
    case 2: // eBatchExpiredStatus['Próximo a vencer']
      return ExpiredColorStatus.expireSoon
    case 3: // eBatchExpiredStatus.Vencido
      return ExpiredColorStatus.expired
    default:
      return ExpiredColorStatus.ok
  }
}
